import React, { useState } from 'react'
import styled from 'styled-components'
import { message, Modal } from 'antd'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import KakaoTemplateCatalog from '../Academy/KakaoTemplateCatalog'

type ButtonType = 'WL' | 'AL' | 'DS' | 'BK' | 'MD'

interface TemplateButton {
	type: ButtonType
	name: string
	linkMo?: string
	linkPc?: string
	linkIos?: string
	linkAnd?: string
}

const BUTTON_TYPE_LABELS: Record<ButtonType, string> = {
	WL: '웹링크',
	AL: '앱링크',
	DS: '배송조회',
	BK: '봇 키워드',
	MD: '메시지 전달',
}

const PageWrapper = styled.div`
	padding: 24px;
	width: 100%;
	min-width: 0;
`

const PageHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	margin-bottom: 20px;
`

const PageTitle = styled.h2`
	font-size: 24px;
	font-weight: 700;
	color: #111111;
	margin: 0;
`

const RegisterButton = styled.button`
	padding: 8px 16px;
	border-radius: 6px;
	border: 1px solid #1890ff;
	background-color: #1890ff;
	color: #fff;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;

	&:hover:not(:disabled) {
		background-color: #40a9ff;
		border-color: #40a9ff;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

const FormRow = styled.div`
	margin-bottom: 16px;

	label {
		display: block;
		margin-bottom: 6px;
		font-weight: 500;
		font-size: 14px;
		color: #333;
	}

	input,
	textarea,
	select {
		width: 100%;
		padding: 8px 12px;
		border: 1px solid #d9d9d9;
		border-radius: 4px;
		font-size: 14px;
	}

	textarea {
		min-height: 120px;
		resize: vertical;
	}
`

const ButtonBlock = styled.div`
	border: 1px solid #e8e8e8;
	border-radius: 6px;
	padding: 12px;
	margin-bottom: 12px;
	background: #fafafa;
`

const ButtonRow = styled.div`
	display: flex;
	gap: 8px;
	align-items: flex-start;
	margin-bottom: 8px;

	&:last-child {
		margin-bottom: 0;
	}
`

const SmallButton = styled.button`
	padding: 4px 10px;
	font-size: 12px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;
	background: #fff;
	cursor: pointer;
	white-space: nowrap;

	&.danger {
		border-color: #ff4d4f;
		color: #ff4d4f;
	}
`

const MAX_BUTTONS = 5

export const AlimtalkTemplatePage: React.FC = () => {
	const { data: token } = useToken()
	const [refreshKey, setRefreshKey] = useState(0)
	const [modalOpen, setModalOpen] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [templateContent, setTemplateContent] = useState('')
	const [buttons, setButtons] = useState<TemplateButton[]>([])
	const [submitting, setSubmitting] = useState(false)

	const handleOpenRegister = () => {
		setTemplateName('')
		setTemplateContent('')
		setButtons([])
		setModalOpen(true)
	}

	const handleCloseModal = () => {
		if (!submitting) setModalOpen(false)
	}

	const addButton = () => {
		if (buttons.length >= MAX_BUTTONS) {
			message.warning(`버튼은 최대 ${MAX_BUTTONS}개까지 등록할 수 있습니다.`)
			return
		}
		setButtons(b => [...b, { type: 'WL', name: '', linkMo: '', linkPc: '' }])
	}

	const removeButton = (index: number) => {
		setButtons(b => b.filter((_, i) => i !== index))
	}

	const updateButton = (index: number, field: keyof TemplateButton, value: string) => {
		setButtons(b => {
			const next = [...b]
			const cur: TemplateButton = { ...next[index], [field]: value }
			if (field === 'type') {
				const type = value as ButtonType
				if (type === 'WL') {
					cur.linkMo = cur.linkMo ?? ''
					cur.linkPc = cur.linkPc ?? ''
					cur.linkIos = undefined
					cur.linkAnd = undefined
				} else if (type === 'AL') {
					cur.linkIos = cur.linkIos ?? ''
					cur.linkAnd = cur.linkAnd ?? ''
					cur.linkMo = undefined
					cur.linkPc = undefined
				} else {
					cur.linkMo = cur.linkPc = cur.linkIos = cur.linkAnd = undefined
				}
			}
			next[index] = cur
			return next
		})
	}

	const buildPayloadButtons = (): Array<{ type: ButtonType; name: string; linkMo?: string; linkPc?: string; linkIos?: string; linkAnd?: string }> | undefined => {
		const valid = buttons
			.map(b => {
				const name = b.name.trim()
				if (!name) return null
				const out: any = { type: b.type, name }
				if (b.type === 'WL' && b.linkMo?.trim() && b.linkPc?.trim()) {
					out.linkMo = b.linkMo.trim()
					out.linkPc = b.linkPc.trim()
				}
				if (b.type === 'AL' && b.linkIos?.trim() && b.linkAnd?.trim()) {
					out.linkIos = b.linkIos.trim()
					out.linkAnd = b.linkAnd.trim()
				}
				return out
			})
			.filter((b): b is NonNullable<typeof b> => b !== null)
		if (valid.length === 0) return undefined
		return valid
	}

	const handleSubmitRegister = async () => {
		const name = templateName.trim()
		const content = templateContent.trim()
		if (!name) {
			message.warning('템플릿명을 입력해주세요.')
			return
		}
		if (!content) {
			message.warning('템플릿 내용을 입력해주세요.')
			return
		}
		if (!token) {
			message.error('로그인이 필요합니다.')
			return
		}
		const payloadButtons = buildPayloadButtons()
		if (payloadButtons) {
			for (let i = 0; i < payloadButtons.length; i++) {
				const b = payloadButtons[i]
				if (b.type === 'WL' && (!b.linkMo || !b.linkPc)) {
					message.warning(`버튼 ${i + 1}: 웹링크는 모바일/PC 링크를 모두 입력해주세요.`)
					return
				}
				if (b.type === 'AL' && (!b.linkIos || !b.linkAnd)) {
					message.warning(`버튼 ${i + 1}: 앱링크는 iOS/Android 링크를 모두 입력해주세요.`)
					return
				}
			}
		}

		setSubmitting(true)
		try {
			const res: any = await API.post('/message/templates', token + '', {
				templateName: name,
				templateContent: content,
				...(payloadButtons && payloadButtons.length > 0 ? { buttons: payloadButtons } : {}),
			})
			if (res?.code === 0) {
				message.success('템플릿이 등록되었습니다. IWINV 검수 후 사용 가능합니다.')
				setModalOpen(false)
				setRefreshKey(k => k + 1)
			} else {
				message.error(res?.message || '템플릿 등록에 실패했습니다.')
			}
		} catch (err: any) {
			message.error(err?.message || '템플릿 등록 중 오류가 발생했습니다.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<PageWrapper>
			<PageHeader>
				<PageTitle>알림톡 템플릿</PageTitle>
				<RegisterButton type="button" onClick={handleOpenRegister}>
					템플릿 등록
				</RegisterButton>
			</PageHeader>

			<KakaoTemplateCatalog refreshKey={refreshKey} />

			<Modal
				title="알림톡 템플릿 등록"
				open={modalOpen}
				onCancel={handleCloseModal}
				onOk={handleSubmitRegister}
				okText="등록"
				cancelText="취소"
				confirmLoading={submitting}
				destroyOnClose
				width={560}
			>
				<FormRow>
					<label>템플릿명</label>
					<input
						type="text"
						value={templateName}
						onChange={e => setTemplateName(e.target.value)}
						placeholder="예: 결시 안내 (최대 15자)"
					/>
				</FormRow>
				<FormRow>
					<label>템플릿 내용</label>
					<textarea
						value={templateContent}
						onChange={e => setTemplateContent(e.target.value)}
						placeholder="발송될 메시지 내용을 입력하세요. 변수는 #{변수명} 형식으로 사용할 수 있습니다."
					/>
				</FormRow>
				<FormRow>
					<label>버튼 (선택, 최대 5개)</label>
					{buttons.map((btn, idx) => (
						<ButtonBlock key={idx}>
							<ButtonRow>
								<select
									value={btn.type}
									onChange={e => updateButton(idx, 'type', e.target.value as ButtonType)}
									style={{ width: 120 }}
								>
									{(Object.keys(BUTTON_TYPE_LABELS) as ButtonType[]).map(t => (
										<option key={t} value={t}>{BUTTON_TYPE_LABELS[t]}</option>
									))}
								</select>
								<input
									placeholder="버튼명 (최대 15자)"
									value={btn.name}
									onChange={e => updateButton(idx, 'name', e.target.value)}
									style={{ flex: 1 }}
								/>
								<SmallButton type="button" className="danger" onClick={() => removeButton(idx)}>삭제</SmallButton>
							</ButtonRow>
							{btn.type === 'WL' && (
								<>
									<ButtonRow>
										<input
											placeholder="모바일 웹 링크 (linkMo)"
											value={btn.linkMo ?? ''}
											onChange={e => updateButton(idx, 'linkMo', e.target.value)}
										/>
									</ButtonRow>
									<ButtonRow>
										<input
											placeholder="PC 웹 링크 (linkPc)"
											value={btn.linkPc ?? ''}
											onChange={e => updateButton(idx, 'linkPc', e.target.value)}
										/>
									</ButtonRow>
								</>
							)}
							{btn.type === 'AL' && (
								<>
									<ButtonRow>
										<input
											placeholder="iOS 앱 링크 (linkIos)"
											value={btn.linkIos ?? ''}
											onChange={e => updateButton(idx, 'linkIos', e.target.value)}
										/>
									</ButtonRow>
									<ButtonRow>
										<input
											placeholder="Android 앱 링크 (linkAnd)"
											value={btn.linkAnd ?? ''}
											onChange={e => updateButton(idx, 'linkAnd', e.target.value)}
										/>
									</ButtonRow>
								</>
							)}
						</ButtonBlock>
					))}
					{buttons.length < MAX_BUTTONS && (
						<SmallButton type="button" onClick={addButton}>+ 버튼 추가</SmallButton>
					)}
				</FormRow>
				<p style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
					등록 후 IWINV 검수(2~3 영업일)를 거쳐 사용 가능합니다. 버튼 타입: 웹링크(WL), 앱링크(AL), 배송조회(DS), 봇키워드(BK), 메시지전달(MD).
				</p>
			</Modal>
		</PageWrapper>
	)
}

export default AlimtalkTemplatePage
